/**
 * Binary FPMM math - mirrors `prediction-market/src/fpmm.rs` bit-for-bit
 * (mint-then-swap buy, integer-sqrt sell). Kept free of DOM / chain imports
 * so `fpmm-parity.test.mjs` can exercise the same helpers the trade ticket uses.
 */

/** Spot P(yes) = no / (yes + no). */
export function spotPrices(yesReserve, noReserve) {
  const yes = Number(yesReserve);
  const no = Number(noReserve);
  const sum = yes + no;
  if (!sum || Number.isNaN(sum)) return { pYes: null, pNo: null };
  const pYes = no / sum;
  return { pYes, pNo: 1 - pYes };
}

/** Floor integer square root via Newton's method (BigInt). Mirrors `isqrt` in fpmm.rs. */
function isqrtBigInt(n) {
  if (n < 2n) return n;
  let x = n;
  let y = (x + 1n) / 2n;
  while (y < x) {
    x = y;
    y = (x + n / x) / 2n;
  }
  return x;
}

/**
 * Exact FPMM buy (mint-then-swap): shares = a * (same + other + a) / (other + a).
 * Mirrors `buy_shares` in prediction-market/src/fpmm.rs.
 */
export function buySharesOut(yesReserve, noReserve, outcome, collateralLux) {
  const yes = BigInt(yesReserve);
  const no = BigInt(noReserve);
  const amt = BigInt(collateralLux);
  if (amt <= 0n || yes <= 0n || no <= 0n) return null;
  const same = outcome === 0 ? yes : no;
  const other = outcome === 0 ? no : yes;
  const shares = (amt * (same + other + amt)) / (other + amt);
  if (shares <= 0n || shares >= same) return null;
  return shares;
}

/**
 * Exact FPMM sell (burn a partial complete set). Mirrors `sell_collateral`.
 */
export function sellCollateralOut(yesReserve, noReserve, outcome, sharesIn) {
  const yes = BigInt(yesReserve);
  const no = BigInt(noReserve);
  const shares = BigInt(sharesIn);
  if (shares <= 0n || yes <= 0n || no <= 0n) return null;
  const same = outcome === 0 ? yes : no;
  const other = outcome === 0 ? no : yes;

  const b = same + other + shares;
  const disc = b * b - 4n * shares * other;
  if (disc < 0n) return null;
  const root = isqrtBigInt(disc);
  const out = (b - root) / 2n;
  if (out <= 0n || out >= other) return null;
  return out;
}

/** Post-buy reserves after mint-then-swap. Returns `{ yes, no, shares }` or null. */
export function applyBuyReserves(yesReserve, noReserve, outcome, collateralLux) {
  const shares = buySharesOut(yesReserve, noReserve, outcome, collateralLux);
  if (shares == null) return null;
  const yes = BigInt(yesReserve);
  const no = BigInt(noReserve);
  const a = BigInt(collateralLux);
  if (outcome === 0) {
    return { yes: yes + a - shares, no: no + a, shares };
  }
  return { yes: yes + a, no: no + a - shares, shares };
}

/** Post-sell reserves. Returns `{ yes, no, collateral }` or null. */
export function applySellReserves(yesReserve, noReserve, outcome, sharesIn) {
  const collateral = sellCollateralOut(yesReserve, noReserve, outcome, sharesIn);
  if (collateral == null) return null;
  const yes = BigInt(yesReserve);
  const no = BigInt(noReserve);
  const s = BigInt(sharesIn);
  if (outcome === 0) {
    return { yes: yes + s - collateral, no: no - collateral, collateral };
  }
  return { yes: yes - collateral, no: no + s - collateral, collateral };
}

/**
 * Absolute change in the traded outcome's spot price, in basis points of
 * probability (1 bps = 0.01%). Null if reserves/trade invalid.
 */
export function priceImpactBps(yesReserve, noReserve, outcome, isBuy, amount) {
  const before = spotPrices(yesReserve, noReserve);
  const spotBefore = outcome === 0 ? before.pYes : before.pNo;
  if (spotBefore == null) return null;

  let afterRes;
  if (isBuy) {
    afterRes = applyBuyReserves(yesReserve, noReserve, outcome, amount);
  } else {
    afterRes = applySellReserves(yesReserve, noReserve, outcome, amount);
  }
  if (afterRes == null) return null;

  const after = spotPrices(afterRes.yes, afterRes.no);
  const spotAfter = outcome === 0 ? after.pYes : after.pNo;
  if (spotAfter == null) return null;

  return Math.round(Math.abs(spotAfter - spotBefore) * 10000);
}

/** Avg effective price as probability in [0,1]: spend/shares (buy) or out/shares (sell). */
export function avgEffectivePrice(spendOrOutLux, shares) {
  const spend = BigInt(spendOrOutLux);
  const sh = BigInt(shares);
  if (spend <= 0n || sh <= 0n) return null;
  // Fixed-point 1e6: (spend * 1e6) / shares → divide by 1e6 for float
  const fp = Number((spend * 1_000_000n) / sh) / 1_000_000;
  return fp;
}
